import React from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CATEGORY_ICONS } from "../../../data/demoNetworkData";

export default function InsightDetailModal({ insight, isOpen, onClose }) {
  if (!insight || !isOpen) return null;

  const { detail } = insight;
  const maxChartValue = detail && detail.chartData.length > 0 
    ? Math.max(...detail.chartData.map(d => d.value)) 
    : 1;
  
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  const handleCloseClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return '↑';
      case 'down': return '↓';
      default: return '→';
    }
  };

  const getTrendLabel = (trend) => {
    switch (trend) {
      case 'up': return 'Improving';
      case 'down': return 'Declining';
      default: return 'Stable';
    }
  };

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="insight-modal-wrapper">
          <motion.div
            className="insight-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleOverlayClick}
          />
          <motion.div
            className="insight-modal"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <button className="modal-close-btn" onClick={handleCloseClick} type="button">×</button>
            
            <div className="modal-header">
              <span className="modal-icon">{CATEGORY_ICONS[insight.type]}</span>
              <h2 className="modal-title">{insight.title}</h2>
              {insight.aiGenerated && <span className="ai-badge-large">AI Generated</span>}
            </div>

            <p className="modal-description">{insight.description}</p>

            <div className={`modal-trend ${insight.trend}`}>
              <span className="trend-icon">{getTrendIcon(insight.trend)}</span>
              <span>{getTrendLabel(insight.trend)}</span>
            </div>

            {detail && (
              <>
                <div className="modal-section">
                  <h3 className="section-title">Trend Analysis</h3>
                  <div className="chart-container">
                    {detail.chartData.map((item, index) => (
                      <div key={index} className="chart-bar-wrapper">
                        <div 
                          className="chart-bar"
                          style={{ height: `${(item.value / maxChartValue) * 100}%` }}
                        >
                          <span className="chart-value">{item.value}</span>
                        </div>
                        <span className="chart-label">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="modal-section">
                  <h3 className="section-title">Your Position</h3>
                  <div className="comparison-container">
                    <div className="comparison-bar-bg">
                      <div 
                        className="comparison-bar-fill"
                        style={{ width: `${detail.comparison.percentile}%` }}
                      />
                      <div 
                        className="comparison-marker"
                        style={{ left: `${detail.comparison.percentile}%` }}
                      >
                        <span className="marker-label">You</span>
                      </div>
                    </div>
                    <div className="comparison-labels">
                      <span>0%</span>
                      <span className="percentile-text">Top {100 - detail.comparison.percentile}%</span>
                      <span>100%</span>
                    </div>
                  </div>
                </div>

                <div className="modal-section">
                  <h3 className="section-title">Key Statistics</h3>
                  <div className="stats-grid">
                    {detail.additionalStats.map((stat, index) => (
                      <div key={index} className="stat-item">
                        <span className="stat-value">{stat.value}</span>
                        <span className="stat-label">{stat.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="modal-section">
                  <h3 className="section-title">
                    <span className="ai-icon">✨</span>
                    AI Recommendations
                  </h3>
                  <ul className="recommendations-list">
                    {detail.recommendations.map((rec, index) => (
                      <li key={index} className="recommendation-item">
                        <span className="rec-bullet">→</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
  
  return ReactDOM.createPortal(modalContent, document.body);
}
